import type { Component, Ref } from "vue";
import type { GridViewBlock } from "../view/types";

export type TenantInheritanceFieldConfig = {
  computedKey: string;
  parentKey: string;
  /**
   * API object path when keys live on a nested entity (e.g. `prematchGame`).
   * Inferred from the dialog group prefix when omitted (`prematchGame.status` → `prematchGame`).
   */
  entityPath?: string;
  /** Key used in the PATCH body. Defaults to the field `name`. */
  patchKey?: string;
};

export type FieldValidator = (
  value: unknown,
  values: Record<string, unknown>,
) => string | undefined;

/**
 * Derive a field’s initial value from the full raw `initialValues` row (same idea as grid `valueFormatter`).
 */
export type DialogFieldInitialBinding = {
  valueFormatter?: (row: unknown) => unknown;
  inheritance?: TenantInheritanceFieldConfig;
};

export type SelectOption = {
  value: unknown;
  title: string;
  icon?: { code: string; color: string };
};

/**
 * Passed to `SelectField.options` when it is an async factory.
 * Read at load time so you see the latest form state (starts as the `initialValues` clone, then user edits).
 */
export type DialogSelectOptionsContext = {
  values: Record<string, unknown>;
  /** Source row when the dialog was opened via row-action; undefined for FAB-only dialogs. */
  row?: Record<string, unknown>;
};

export type TextField = {
  type: "text-field";
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  /** Passed through to `NXGDSTextField` (e.g. `"number"`). */
  inputType?: string;
  flex?: number;
  validate?: FieldValidator;
} & DialogFieldInitialBinding;

export type SelectField = {
  type: "select-field";
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Static rows, or `(ctx) => Promise<…>` — use `ctx.values` / `ctx.row` (see `DialogSelectOptionsContext`). */
  options: SelectOption[] | ((ctx: DialogSelectOptionsContext) => Promise<SelectOption[]>);
  /** In-list search on `NXGDSSelect` (same as grid `SelectFilter.searchable`). */
  searchable?: boolean;
  defaultValue?: unknown;
  flex?: number;
  validate?: FieldValidator;
} & DialogFieldInitialBinding;

export type ArrayField = {
  type: "array-field";
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  children?: DialogContentNode[];
  flex?: number;
  validate?: FieldValidator;
} & DialogFieldInitialBinding;

/**
 * Leaf fields allowed inside each structured-array row (repeat per row like a flat `group`).
 * Keys are each field’s `name` on the row object (API body).
 */
export type StructuredArrayRowField = TextField | SelectField | ArrayField;

/**
 * Array of objects with a fixed row layout (same leaf defs every row), min/max length, + on the first row, X on extra rows.
 */
export type StructuredArrayField = {
  type: "structured-array-field";
  name: string;
  label: string;
  required?: boolean;
  minItems: number;
  maxItems: number;
  children: StructuredArrayRowField[];
  flex?: number;
  validate?: FieldValidator;
} & DialogFieldInitialBinding;

/** Replaces any node with a custom Vue component (non-translation). */
export type GenericComponentNode = {
  type: "component";
  component: () => Promise<{ default: Component }>;
  props?: Record<string, unknown>;
};

export type TranslationsPersistenceMode = "isolated" | "main";

export type ParentSubmitUnsavedTranslationsPolicy = "block" | "autosave" | "prompt";

/** Registered by translation tabs for parent submit orchestration (see `resolveTranslationSubmitGates`). */
export type TranslationSubmitGate = {
  isDirty: () => boolean;
  saveDraft: () => Promise<void>;
  discardDraft?: () => void;
  policy: ParentSubmitUnsavedTranslationsPolicy;
  persistence: TranslationsPersistenceMode;
};

/** Passed to translation `get` / `save` adapters (see `TranslationsDialog.vue`). */
export type TranslationsDialogApiContext = {
  row?: Record<string, unknown>;
  values: Record<string, unknown>;
  props: Record<string, unknown>;
};

/** Default canonical shape for the shared translations UI. */
export type TranslationsModel = {
  locales: string[];
  entries: Array<{ key: string; byLocale: Record<string, string> }>;
};

export type TranslationDialogApiIsolated = {
  get: (ctx: TranslationsDialogApiContext) => Promise<unknown>;
  save: (model: unknown, ctx: TranslationsDialogApiContext) => Promise<void>;
};

export type TranslationDialogApiMain = {
  get?: (ctx: TranslationsDialogApiContext) => Promise<unknown>;
  save?: (model: unknown, ctx: TranslationsDialogApiContext) => Promise<void>;
};

/**
 * Static field list, or a factory invoked when the translations surface mounts
 * (mirrors grid `dynamicColumnSlots.createColumns` timing so supplementals are loaded).
 */
export type TranslationFieldsSource = DialogContentNode[] | (() => DialogContentNode[]);

/** First-class tabbed entry: translations surface (see `TabbedStructure`). */
export type TranslationsTabIsolated = {
  kind: "translations";
  id: string;
  label: string;
  persistence: "isolated";
  fields: TranslationFieldsSource;
  props?: Record<string, unknown>;
  api: TranslationDialogApiIsolated;
  parentSubmitWithUnsavedTranslations: ParentSubmitUnsavedTranslationsPolicy;
};

export type TranslationsTabMain = {
  kind: "translations";
  id: string;
  label: string;
  persistence: "main";
  fields?: TranslationFieldsSource;
  props?: Record<string, unknown>;
  api: TranslationDialogApiMain;
  parentSubmitWithUnsavedTranslations?: ParentSubmitUnsavedTranslationsPolicy;
  /**
   * When `api.save` is omitted, `saveDraft` copies the canonical model into `values` at this path
   * (dot notation) so `DialogConfig.api.submit` can persist it.
   */
  syncToValuesPath?: string;
};

export type TranslationsTab = TranslationsTabIsolated | TranslationsTabMain;

export type GridSubmitGate = {
  hasSuccessfulLoad: () => boolean;
  isDirty: () => boolean;
  validateForDialogSubmit: () => boolean;
  buildMergeFragment: () => Record<string, unknown> | null;
  /** Reset dirty baseline after a successful tab save + refetch. */
  markClean?: () => void;
};

export type GridSubmitGateContext = {
  rows: Ref<Record<string, unknown>[]>;
  hasLoadedOnce: Ref<boolean>;
  fetch: () => Promise<void>;
};

export type DialogTabSaveContext = {
  values: Record<string, unknown>;
  /** Snapshot of initial values at dialog open — for dirty detection on tab save. */
  baseline: Record<string, unknown>;
  row?: Record<string, unknown>;
};

export type GridTabSaveContext = DialogTabSaveContext & {
  gate: GridSubmitGate | null;
  /** Grid rows ref from the active tab host; set when the tab is mounted. */
  rows?: Ref<Record<string, unknown>[]>;
};

export type DialogTabSaveHandler = (ctx: DialogTabSaveContext) => Promise<void>;

export type GridTab = {
  kind: "grid";
  id: string;
  label: string;
  /** Static grid config. Use `createGrid` when the block depends on dialog field values. */
  grid?: GridViewBlock;
  /** Build grid config from current dialog values (e.g. entity id for `api.get`). */
  createGrid?: (getValues: () => Record<string, unknown>) => GridViewBlock;
  createSubmitGate?: (grid: GridSubmitGateContext) => GridSubmitGate;
  addRowLabel?: string;
  onAddRow?: (ctx: GridSubmitGateContext) => void;
  save?: (ctx: GridTabSaveContext) => Promise<void>;
};

export function isGridTab(tab: DialogTab): tab is GridTab {
  return "kind" in tab && (tab as { kind?: string }).kind === "grid";
}

export type ComponentNode = GenericComponentNode;

/** Recursive container — children can be fields or nested groups. */
export type DialogGroup = {
  type: "group";
  name: string;
  label: string;
  children: DialogContentNode[];
};

export type DialogContentNode =
  | TextField
  | SelectField
  | ArrayField
  | StructuredArrayField
  | DialogGroup
  | ComponentNode;

export type ContentTab = {
  id: string;
  label: string;
  children: DialogContentNode[];
  save?: DialogTabSaveHandler;
};

export type DialogTab = ContentTab | TranslationsTab | GenericComponentNode | GridTab;

export function isTranslationsTab(tab: DialogTab): tab is TranslationsTab {
  return "kind" in tab && tab.kind === "translations";
}

export type DialogStep =
  | { id: string; label?: string; children: DialogContentNode[] }
  | ComponentNode;

export type TabbedStructure = {
  type: "tabbed";
  tabs: DialogTab[];
  /** Footer Save runs the active tab's `save` handler instead of `DialogConfig.api.submit`. */
  savePerTab?: boolean;
};
export type StepperStructure = { type: "stepper"; steps: DialogStep[] };

export type FlatStructure = { type: "flat"; children: DialogContentNode[] };
export type DialogStructure = TabbedStructure | StepperStructure | FlatStructure | ComponentNode;

export type PopupLayout = { type: "popup" };
export type PopoverLayout = { type: "popover" };

/** Button style for `layout.type === 'nxgds-dialog'` footer actions (matches DS button variants). */
export type NxgdsDialogActionVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "danger"
  | "warning";

/**
 * - `dismiss` — close the dialog only (no `api.submit`).
 * - `submit` — run `api.submit` (then close + refetch on success).
 */
export type NxgdsDialogActionIntent = "dismiss" | "submit";

export type NxgdsDialogLayoutAction = {
  label: string;
  variant?: NxgdsDialogActionVariant;
  intent: NxgdsDialogActionIntent;
};

/**
 * Design-system modal (`NXGDSDialog`): fixed overlay, title, and footer `actions`.
 * Provide any number of buttons in display order; each declares how it behaves via `intent`.
 * When `actions` is omitted or empty, defaults to Cancel (dismiss) + Confirm (submit).
 */
export type NxgdsDialogLayout = {
  type: "nxgds-dialog";
  actions?: NxgdsDialogLayoutAction[];
};

export type DialogLayout = PopupLayout | PopoverLayout | NxgdsDialogLayout;

export type FabTrigger = { type: "fab" };
export type RowActionTrigger = { type: "row-action"; label: string; icon?: string };
export type DialogTrigger = FabTrigger | RowActionTrigger;

export type DialogSubmitContext = {
  row?: Record<string, unknown>;
  getGridSubmitGate?: () => GridSubmitGate | null;
  /**
   * When a nested dialog is opened from a grid `DialogHost`, the parent grid's `delete`
   * (local or API). Omitted for dialogs outside a grid (e.g. category delete on main grid).
   */
  deleteGridRow?: (id: unknown) => Promise<void>;
};

export type DialogApi = {
  submit: (data: Record<string, unknown>, ctx: DialogSubmitContext) => Promise<unknown>;
  /**
   * Called when the dialog opens via row-action. Can return a plain object
   * (sync, row already has all data) or a Promise (async fetch by row.id).
   * When async, the dialog shows a loading state before the form mounts.
   * Omit for create dialogs triggered via FAB.
   */
  initialValues?: (
    row: Record<string, unknown>,
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
};

export type DialogConfig = {
  id: string;
  title: string;
  description?: string | ((row?: Record<string, unknown>) => string);
  /**
   * Use `{ type: "fab" }` for FAB-triggered dialogs. Dialogs opened only from sticky row actions
   * may omit `trigger`.
   */
  trigger?: DialogTrigger;
  layout: DialogLayout;
  structure: DialogStructure;
  api: DialogApi;
  /** When `false`, `api.submit` success does not trigger the grid refetch. Default: `true`. */
  refetchAfterSubmit?: boolean;
  onSuccess?: (result: unknown) => void;
  onError?: (error: unknown) => void;
};
